import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack, Section } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button } from 'tio-ui/components/buttons';
import { t, type IntlService } from 'ember-intl';
import { PhSparkle, PhArrowRight } from 'ember-phosphor-icons';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import StatePill from 'tio-employee/components/reimagined/tas/state-pill';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type {
  DataSourceInfo,
  LiveTasData,
  LiveTasProgram,
} from 'tio-employee/services/reimagined-data';

interface TasProgramsModel {
  tasData: LiveTasData;
  dataSourceInfo: DataSourceInfo;
}

interface TasProgramsSignature {
  Args: {
    model: TasProgramsModel;
  };
}

export default class CareerPathwaysTasProgramsIndex extends Component<TasProgramsSignature> {
  @service declare intl: IntlService;
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get programs(): LiveTasProgram[] {
    return this.args.model.tasData.programs;
  }

  <template>
    <VStack class="gap-6">
      <div
        class="rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 p-6 text-white shadow-lg"
      >
        <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div class="max-w-2xl">
            <div
              class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/80"
            >
              <PhSparkle class="h-4 w-4" />
              {{t "tuition_assistance.reimagined.programs.hero_eyebrow"}}
            </div>
            <Heading @level={{1}} class="text-white">
              {{t "tuition_assistance.reimagined.programs.hero_title"}}
            </Heading>
            <Text class="text-white/80">
              {{t "tuition_assistance.reimagined.programs.hero_subtitle"}}
            </Text>
          </div>
          <LinkTo @route="authenticated.tas.dashboard">
            <Button @intent="primary">{{t
                "tuition_assistance.reimagined.programs.cta_start"
              }}</Button>
          </LinkTo>
        </div>
      </div>

      <DataIndicator @source={{this.tasSource}} @showBadge={{true}} @dataSourceId="tas-programs">
        <Section>
          <:header>
            <Heading @level={{2}}>{{t
                "tuition_assistance.reimagined.programs.section_active"
              }}</Heading>
          </:header>
          <:body>
            {{#if this.programs.length}}
              <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {{#each this.programs as |program|}}
                  <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div class="flex items-start justify-between gap-4">
                      <div>
                        <Heading @level={{3}}>{{program.name}}</Heading>
                        <Text @intent="muted">{{program.institution}}</Text>
                        {{#if program.startDate}}
                          <Text @intent="muted">
                            {{t
                              "tuition_assistance.reimagined.programs.label_starts"
                              date=program.startDate
                            }}
                          </Text>
                        {{/if}}
                      </div>
                      <StatePill @state={{program.state}} />
                    </div>
                    <div class="mt-4">
                      <LinkTo
                        @route="authenticated.tas.programs.show"
                        @model={{program.id}}
                        class="tio-copy text-sm font-semibold"
                      >
                        {{t "tuition_assistance.reimagined.programs.link_open"}}
                        <PhArrowRight class="ml-1 inline h-4 w-4" />
                      </LinkTo>
                    </div>
                  </div>
                {{/each}}
              </div>
            {{else}}
              <div class="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8">
                <Heading @level={{3}}>{{t
                    "tuition_assistance.reimagined.programs.empty_title"
                  }}</Heading>
                <Text @intent="muted">
                  {{t "tuition_assistance.reimagined.programs.empty_body"}}
                </Text>
                <div class="mt-4">
                  <LinkTo @route="authenticated.tas.dashboard">
                    <Button @intent="primary">{{t
                        "tuition_assistance.reimagined.programs.empty_cta"
                      }}</Button>
                  </LinkTo>
                </div>
              </div>
            {{/if}}
          </:body>
        </Section>
      </DataIndicator>
    </VStack>
  </template>
}
